<template>
    <Layout class="course-page" :ready="ready" :breadcrumb="breadcrumbItems">
        <template v-if="ready">
            <div class="mb-4">
                <div class="row justify-content-between">
                    <div class="col" style="max-width: 900px">
                        <div class="mt-2 mt-md-3">
                            <template v-if="course.partner">
                                <div class="mb-2 mb-md-3">
                                    <div class="row align-items-center gx-0">
                                        <div class="col-auto">
                                            <img class="d-block course-page__partner-image"
                                                :class="{ 'course-page__partner-image--small':course.partner.legend }"
                                                :src="course.partner.thumbnail"
                                                :alt="course.partner.legend || 'partenaire'"
                                            >
                                        </div>
                                        <template v-if="course.partner.legend">
                                            <div class="col">
                                                <h3 class="course-page__partner-title text-body mb-0">
                                                    {{ course.partner.legend }}
                                                </h3>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </template>
                            <template v-if="course.is_qualiopi">
                                <div class="d-flex mb-2 mb-md-3">
                                    <div class="bg-primary text-white px-2 course-page__category mb-0" style="padding: .125em .25em">
                                        formation
                                    </div>
                                </div>
                            </template>
                            <template v-if="course.surtitle">
                                <h2 class="course-page__category text-primary">{{ course.surtitle }}</h2>
                            </template>
                            <h1 class="content-title course-page__title mb-3 px-0" :class="{ 'course-page__title--has-surtitle': course.surtitle }">
                                {{ course.title }}
                            </h1>
                            <template v-if="course.subtitle">
                                <h2 class="course-page__subtitle d-inline-block text-white bg-primary mb-3 mb-md-4">
                                    {{ course.subtitle }}
                                </h2>
                            </template>
                            <div class="row g-3">
                                <div class="col-md order-1 order-md-0">
                                    <template v-if="course.description">
                                        <div class="course-page__description cursus-content"
                                            :class="descriptionClasses"
                                            v-html="course.description"
                                            ref="description"
                                        ></div>
                                        <template v-if="hasShowMore && !expandDescription">
                                            <div class="mt-2 d-print-none">
                                                <a href="#" @click.prevent="handleShowMoreClicked">
                                                    en savoir <span style="font-size: .875em">＋</span>
                                                </a>
                                            </div>
                                        </template>
                                    </template>
                                </div>
                                <template v-if="course.visual">
                                    <div class="col-md-auto d-lg-none course-page__col--image">
                                        <CourseVisual :course="course" image-class="course-page__image" />
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="col-md col-lg-auto d-none d-lg-block course-page__col--image">
                        <div class="text-right mb-2 d-none d-md-block d-print-none">
                            <button class="btn btn-link btn-sm" @click="handlePrintClicked">imprimer la formation</button>
                        </div>
                        <template v-if="course.visual">
                            <CourseVisual :course="course" image-class="course-page__image" />
                            <template v-if="course.visual.legend">
                                <div class="mt-1" style="font-size: .75rem">
                                    {{ course.visual.legend }}
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
            </div>

            <CourseInfos :course="course" />

            <template v-if="!course.is_free">
                <div class="alert alert-info mb-4">
                    pour un entretien préalable en vue d’évaluer la pertinence du choix de la formation, contacter le(s) <strong>contact(s) pédagogique(s)</strong>
                </div>
            </template>

            <CourseSections :course="course" />

            <PartnersInformation :course="course" />

            <Footer
                :items="footerItems"
            />
        </template>
        <template v-else-if="errorMessage">
            <div class="alert alert-danger mt-3">
                {{ errorMessage }}
            </div>
        </template>
        <template v-else>
            <slot />
        </template>
    </Layout>
</template>

<script>
  import { getCourse } from "../../api";
  import Layout from "../Layout";
  import CourseTag from "../ui/CourseTag";
  import CourseInfos from "./course/CourseInfos";
  import CourseSections from "./course/CourseSections";
  import PartnersInformation from "./course/PartnersInformation";
  import Collapse from "../ui/Collapse";
  import { courseBreadcrumb } from "../../util/breadcrumb";
  import { updateDocumentTitle } from "../../util/title";
  import { baseUrl } from "../../util/config";
  import CourseVisual from "../CourseVisual";
  import Footer from "../ui/Footer";

  export default {
        components: {
            Footer,
            CourseVisual,
            PartnersInformation,
            Collapse,
            CourseSections,
            CourseInfos,
            CourseTag,
            Layout
        },
        props: {
            showBreadcrumb: Boolean,
            breadcrumb: Array,
            footerItems: Array,
            thumbnailWidth: {
                type: Number,
                default: 500,
            },
        },
        data() {
            return {
                ready: false,
                course: this.$attrs.course ?? null, // for the case rendering JSON directly (see /component.js)
                hasShowMore: false,
                expandDescription: false,
                errorMessage: null,
            }
        },
        computed: {
            courseId() {
                return this.$route.params.courseId;
            },
            breadcrumbItems() {
                return this.showBreadcrumb
                    ? [...this.breadcrumb, ...courseBreadcrumb(this.course)]
                    : null;
            },
            descriptionClasses() {
                return {
                    'course-page__description--expand': this.expandDescription,
                }
            },
        },
        methods: {
            baseUrl,
            async init() {
                this.course ??= await getCourse({
                    courseId: this.courseId,
                    thumbnail_width: this.thumbnailWidth,
                }).catch(error => {
                    if(error.response?.status === 403) {
                        this.errorMessage = 'Cette formation n’est pas accessible pour le moment.';
                    }
                    return Promise.reject(error);
                });
                updateDocumentTitle(this.breadcrumbItems);
                this.ready = true;
                await this.$nextTick();
                this.layout();
            },
            handleShowMoreClicked() {
                this.expandDescription = !this.expandDescription;
            },
            handlePrintClicked() {
                window.print();
            },
            layout() {
                if(this.$refs.description) {
                    this.hasShowMore = this.$refs.description.scrollHeight > this.$refs.description.offsetHeight;
                }
            },
        },
        created() {
            this.init();
        },
        mounted() {

        },
    }
</script>
